import React, { Component } from 'react'
import styled from 'styled-components'
import { get } from 'lodash'
import Modal from '../../../../components/Modal'
import Button from '../../../../components/Button'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import History from '../History'
import OpeningDraft from '../OpeningDraft'
import { wpAxios } from '../../../../config/axiosResolver'
import ConfigResolver from '../../../../config/ConfigResolver'

const config = ConfigResolver.config()

/* Sized for the widest tab rather than the average one. History shows two
   columns of prose side by side, and at the original 860px each column was
   narrow enough that a diff wrapped every few words. The panel does not resize
   per tab -- a dialog that changes shape as you move across its own tabs reads
   as a glitch. */
const Panel = styled.div`
  width: 1180px;
  max-width: 94vw;
  height: 84vh;
  display: flex;
  flex-direction: column;
`

const Head = styled.div`
  padding: 22px 30px 0;
`

const Title = styled.div`
  font-family: Hind;
  font-size: 20px;
  color: #000;
`

const Sub = styled.div`
  font-family: Hind;
  font-size: 13px;
  font-weight: 300;
  color: #9F9E9B;
  margin-top: 2px;
`

const Tabs = styled.div`
  display: flex;
  gap: 26px;
  padding: 0 30px;
  margin-top: 16px;
  border-bottom: 1px solid #EDEAE8;
`

/* Underline rather than a pill or a filled block: these are three views of one
   document, not three destinations, and a heavier treatment made the panel read
   like navigation. */
const Tab = styled.div`
  font-family: Hind;
  font-size: 15px;
  padding: 0 2px 11px;
  cursor: ${p => p.$locked ? 'default' : 'pointer'};
  opacity: ${p => p.$locked ? 0.4 : 1};
  color: ${p => p.active ? '#404040' : '#9F9E9B'};
  border-bottom: 2px solid ${p => p.active ? '#ECB595' : 'transparent'};
  margin-bottom: -1px;
  white-space: nowrap;

  &:hover { color: ${p => p.$locked ? '#9F9E9B' : '#404040'}; }
`

const Count = styled.span`
  font-family: Hind;
  font-size: 12px;
  color: #FFF;
  background: #ECB595;
  border-radius: 9px;
  padding: 1px 7px;
  margin-left: 7px;
`

const Body = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

/* Hidden, not unmounted. The opening tab holds unsaved work -- a draft the
   editor has edited by hand, or paid ten seconds for -- and rendering it
   conditionally threw all of it away on a glance at History. Closing a modal
   reads as discarding; clicking a tab does not. Mounting it costs nothing,
   because it fetches nothing until asked. */
const Pane = styled.div`
  display: ${p => p.$active ? 'flex' : 'none'};
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

/* The resting state of the copy tab. Deliberately not auto-run: reaching the
   history now goes through this panel too, and firing a paid model call because
   somebody wanted the audit trail is the wrong default. */
const Intro = styled.div`
  padding: 44px 30px;
  text-align: center;
`

const IntroText = styled.div`
  font-family: Hind;
  font-size: 15px;
  font-weight: 300;
  color: #55575A;
  line-height: 1.6;
  max-width: 430px;
  margin: 0 auto 22px;
`

const Notice = styled.div`
  font-family: Hind;
  font-size: 13px;
  font-weight: 300;
  color: ${p => p.tone === 'bad' ? '#B4776F' : '#55575A'};
  background: #FAFAFA;
  border-left: 2px solid ${p => p.tone === 'bad' ? '#B4776F' : '#ECB595'};
  padding: 10px 13px;
  margin: 0 30px 16px;
  text-align: left;
`

const CopyTools = [
  { key: 'copy', label: 'Clean up copy', needsAssists: true },
  { key: 'opening', label: 'Opening paragraph', needsAssists: true },
  { key: 'history', label: 'History' }
]

/**
 * One place for the three things an editor does to a submission's words.
 *
 * They used to be three separate controls, and split across the sidebar at
 * that: revision history sat among the navigation links while the two AI tools
 * sat at the bottom. Three buttons for one job, and no way to move between them
 * without closing one and hunting for the next.
 *
 * The tools themselves are unchanged — History and OpeningDraft render here
 * embedded, so there is one implementation of each rather than a copy per
 * context.
 */
class EditorTools extends Component {
  constructor (props) {
    super(props)

    this.state = {
      tab: 'copy',
      running: false,
      // Set by whichever tool is embedded when it has a request in flight.
      // This panel owns the modal, so it is the only thing that can refuse to
      // close over a write the editor cannot see.
      childBusy: false,
      // Bumped each time the panel is opened, and used as the opening tab's
      // key. A draft survives tab switches within one visit and does not
      // outlive the visit itself, which is the same rule the rest of this
      // state follows.
      visit: 0,
      suggestionId: null,
      suggestionBase: null,
      rejectedCount: 0,
      notice: null,
      tone: 'info'
    }
  }

  /** A request is in flight somewhere in the panel. */
  busy () {
    return this.state.running || this.state.childBusy
  }

  /**
   * Which tabs this submission can actually use.
   *
   * History works on anything with an id. The two writing tools need a
   * submission new enough to hold the sectioned document and a save path to
   * write through, which legacy submissions and lbb-instagram do not have.
   */
  tabs () {
    return this.props.assistsAvailable === false
      ? CopyTools.filter(t => !t.needsAssists)
      : CopyTools
  }

  /** History when it is the only thing here, otherwise the usual landing tab. */
  firstTab () {
    const available = this.tabs()

    return available.some(t => t.key === 'copy') ? 'copy' : available[0].key
  }

  componentDidUpdate (prev) {
    // A fresh visit starts fresh. Leaving the last run's suggestion on screen
    // would show corrections against a document that has since moved on.
    if (this.props.open && !prev.open) {
      this.setState({
        tab: this.firstTab(), suggestionId: null, suggestionBase: null,
        rejectedCount: 0, notice: null, tone: 'info', childBusy: false,
        visit: this.state.visit + 1
      })
    }
  }

  runCopyEdit () {
    const { submissionId, flushSaves } = this.props

    if (this.state.running) return

    this.setState({ running: true, notice: null })

    // The stored document is what gets corrected, so anything still queued has
    // to land first — otherwise the suggestion is built from text the editor
    // has already replaced.
    const ready = flushSaves ? flushSaves() : Promise.resolve()

    ready.then(
      () => wpAxios.post(`${config.siteUrl}/api/v2/submissions/${submissionId}/ai-copy-edit`)
        .then((res) => {
          const data = get(res, 'data.data', {})
          const rejected = Object.keys(data.rejected || {}).length

          if (data.revisionId) {
            this.setState({
              running: false,
              suggestionId: data.revisionId,
              suggestionBase: data.baseHash || null,
              rejectedCount: rejected,
              notice: null
            })

            return
          }

          this.setState({
            running: false,
            tone: 'info',
            notice: rejected
              ? `No changes proposed. ${rejected} ${rejected === 1 ? 'suggestion was' : 'suggestions were'} rejected for going beyond spelling and punctuation.`
              : 'No spelling, grammar or punctuation corrections found.'
          })
        })
        .catch((err) => {
          this.setState({
            running: false,
            tone: 'bad',
            notice: get(err, 'response.data.message')
              || 'Could not reach the copy editor. Nothing was changed.'
          })
        }),
      () => {
        this.setState({
          running: false,
          tone: 'bad',
          notice: 'Your last edit has not been saved yet, so nothing was checked. Save that change first.'
        })
      }
    )
  }

  renderCopyTab () {
    const { submissionId, onApply } = this.props
    const { running, suggestionId, suggestionBase, notice, tone } = this.state

    if (suggestionId) {
      return (
        <History
          embedded
          open
          handleClose={() => this.setState({ suggestionId: null, suggestionBase: null })}
          submissionId={submissionId}
          suggestionId={suggestionId}
          suggestionBase={suggestionBase}
          onApply={onApply}
          onBusy={(b) => this.setState({ childBusy: b })}
        />
      )
    }

    return (
      <div>
        {notice && <Notice tone={tone}>{notice}</Notice>}
        <Intro>
          <IntroText>
            Checks spelling, grammar and punctuation across every prompt the
            couple answered. It corrects mistakes and leaves their voice alone,
            and you see every change before anything is saved.
          </IntroText>
          <Button
            text='Check spelling and grammar'
            width={240}
            showLoader={running}
            disabled={running}
            onClick={() => this.runCopyEdit()}
          />
        </Intro>
      </div>
    )
  }

  render () {
    const { open, handleClose, submissionId, onApply, onSaveOpening, flushSaves } = this.props
    const { tab, rejectedCount } = this.state
    const busy = this.busy()
    const assists = this.props.assistsAvailable !== false
    const onBusy = (b) => this.setState({ childBusy: b })

    return (
      <ErrorBoundary>
        {/* Held shut while anything is in flight. A save that lands after the
            panel has gone writes to a document the editor stopped watching. */}
        <Modal open={open} handleClose={busy ? () => {} : handleClose}>
          <Panel>
            <Head>
              <Title>Copy tools</Title>
              <Sub>Editor only. Nothing is saved until you accept it.</Sub>
            </Head>

            <Tabs>
              {this.tabs().map(t => (
                // Locked for the same reason, plus one of its own: leaving a
                // tab unmounts it, and a drafting run that nobody is left to
                // receive is a paid request thrown away.
                <Tab
                  key={t.key}
                  active={tab === t.key}
                  $locked={busy && tab !== t.key}
                  onClick={() => busy ? null : this.setState({ tab: t.key })}
                >
                  {t.label}
                  {t.key === 'copy' && rejectedCount > 0 && <Count>{rejectedCount}</Count>}
                </Tab>
              ))}
            </Tabs>

            <Body>
              {tab === 'copy' && assists && this.renderCopyTab()}

              {assists && (
              <Pane $active={tab === 'opening'}>
                <OpeningDraft
                  key={this.state.visit}
                  embedded
                  open={open}
                  handleClose={handleClose}
                  submissionId={submissionId}
                  onSave={onSaveOpening}
                  flushSaves={flushSaves}
                  onBusy={onBusy}
                  existingIntro={this.props.existingIntro}
                />
              </Pane>
              )}

              {tab === 'history' && (
                <History
                  embedded
                  open={open}
                  handleClose={handleClose}
                  submissionId={submissionId}
                  onApply={onApply}
                  onBusy={onBusy}
                />
              )}
            </Body>
          </Panel>
        </Modal>
      </ErrorBoundary>
    )
  }
}

export default EditorTools
